import React, { useState, useRef, useEffect } from 'react';
import styles from '../../Assets/Styles/EmotionWheel.module.css';
import { EXPANDED_EMOTIONS, WHEEL_CONFIG } from './DemoConstants';
import { useMemo } from 'react';

const EmotionWheel = ({ onEmotionSelect, selectedEmotion, availableEmotions }) => {
    const svgRef = useRef(null);
    const updatePendingRef = useRef(false);
    const [isDragging, setIsDragging] = useState(false);
    const [markerPosition, setMarkerPosition] = useState({ x: WHEEL_CONFIG.centerX, y: WHEEL_CONFIG.centerY });
    const [displayEmotion, setDisplayEmotion] = useState(null); // For showing the emotion during dragging
    const [hasUserSetMarker, setHasUserSetMarker] = useState(false);

    // Filter emotions based on availability if availableEmotions is provided
    const filteredEmotions = useMemo(() => {
        return availableEmotions
            ? EXPANDED_EMOTIONS.filter(emotion => availableEmotions.includes(emotion.id))
            : EXPANDED_EMOTIONS;
    }, [availableEmotions]);

    // Function to get coordinates relative to SVG
    const getSVGCoordinates = (event) => {
        const svg = svgRef.current;
        if (!svg) return { x: 0, y: 0 };

        const rect = svg.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        return { x, y };
    };

    // Calculate distance from center
    const getDistanceFromCenter = (x, y) => {
        return Math.sqrt(
            Math.pow(x - WHEEL_CONFIG.centerX, 2) +
            Math.pow(y - WHEEL_CONFIG.centerY, 2)
        );
    };

    // Clamp coordinates to the wheel
    const clampToWheel = (x, y) => {
        const distance = getDistanceFromCenter(x, y);

        if (distance <= WHEEL_CONFIG.radius) {
            return { x, y };
        } else {
            // Normalize to edge of circle
            const angle = Math.atan2(y - WHEEL_CONFIG.centerY, x - WHEEL_CONFIG.centerX);
            const newX = WHEEL_CONFIG.centerX + WHEEL_CONFIG.radius * Math.cos(angle);
            const newY = WHEEL_CONFIG.centerY + WHEEL_CONFIG.radius * Math.sin(angle);
            return { x: newX, y: newY };
        }
    };

    // Find the closest emotion for a given position
    const findClosestEmotion = (position) => {
        let closestEmotion = null;
        let closestDistance = Infinity;

        filteredEmotions.forEach(emotion => {
            const distance = Math.sqrt(
                Math.pow(emotion.position.x - position.x, 2) +
                Math.pow(emotion.position.y - position.y, 2)
            );

            if (distance < closestDistance) {
                closestDistance = distance;
                closestEmotion = emotion;
            }
        });

        return closestEmotion;
    };

    // Mouse event handlers
    const handleMouseDown = (event) => {
        event.preventDefault();
        setIsDragging(true);
        setHasUserSetMarker(true);
        const coords = getSVGCoordinates(event);
        const clampedCoords = clampToWheel(coords.x, coords.y);
        setMarkerPosition(clampedCoords);

        // Update display emotion while dragging
        const closestEmotion = findClosestEmotion(clampedCoords);
        if (closestEmotion) {
            setDisplayEmotion(closestEmotion);
        }
    };

    const handleMouseMove = (event) => {
        if (!isDragging) return;

        const coords = getSVGCoordinates(event);
        const clampedCoords = clampToWheel(coords.x, coords.y);
        setMarkerPosition(clampedCoords);

        // Update display emotion while dragging
        const closestEmotion = findClosestEmotion(clampedCoords);
        if (closestEmotion) {
            setDisplayEmotion(closestEmotion);
        }
    };

    const handleTouchMove = (event) => {
        if (!isDragging || !event.touches[0]) return;

        event.preventDefault();
        const touch = event.touches[0];
        const svg = svgRef.current;
        const rect = svg.getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;

        const clampedCoords = clampToWheel(x, y);
        setMarkerPosition(clampedCoords);

        // Update display emotion while dragging
        const closestEmotion = findClosestEmotion(clampedCoords);
        if (closestEmotion) {
            setDisplayEmotion(closestEmotion);
        }
    };

    const handleMouseUp = (event) => {
        if (!isDragging) return;

        // Find the closest emotion without snapping the marker position
        const closestEmotion = findClosestEmotion(markerPosition);
        if (closestEmotion) {
            onEmotionSelect(closestEmotion.id);
        }

        setIsDragging(false);
        setDisplayEmotion(null);
    };

    const handleTouchEnd = (event) => {
        if (!isDragging) return;

        // Find the closest emotion without snapping the marker position
        const closestEmotion = findClosestEmotion(markerPosition);
        if (closestEmotion) {
            onEmotionSelect(closestEmotion.id);
        }

        setIsDragging(false);
        setDisplayEmotion(null);
    };

    // Initial setup and updates when selectedEmotion changes.
    // Only update the marker position on initial load if the user hasn't manually moved it.
    useEffect(() => {
        if (updatePendingRef.current) return;

        if (!hasUserSetMarker) {
            const selectedEmotionObj = filteredEmotions.find(e => e.id === selectedEmotion);

            if (selectedEmotionObj) {
                setMarkerPosition({
                    x: selectedEmotionObj.position.x,
                    y: selectedEmotionObj.position.y
                });
            } else if (filteredEmotions.length > 0) {
                updatePendingRef.current = true;
                setTimeout(() => {
                    onEmotionSelect(filteredEmotions[0].id);
                    updatePendingRef.current = false;
                }, 50);
            }
        }
    }, [selectedEmotion, filteredEmotions, hasUserSetMarker, onEmotionSelect]);

    // Determine active emotion for display: use displayEmotion if dragging, else based on selectedEmotion.
    const activeEmotion = isDragging && displayEmotion ?
        displayEmotion :
        (filteredEmotions.find(e => e.id === selectedEmotion) ||
            (filteredEmotions.length > 0 ? filteredEmotions[0] : null));

    return (
        <div className={styles['emotion-wheel']}>
            <svg
                ref={svgRef}
                width="300"
                height="380"
                viewBox="0 0 300 380"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onTouchStart={handleMouseDown}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onMouseLeave={() => {
                    if (isDragging) {
                        handleMouseUp();
                    }
                }}
                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            >
                <defs>
                    {/* <radialGradient id="wheelGrad" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#b2a49b" />
                        <stop offset="100%" stopColor="#818283" />
                    </radialGradient> */}

                    <radialGradient id="wheelGrad" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#9dcbde" />
                        <stop offset="100%" stopColor="#9dcbde" />
                    </radialGradient>



                    {/* Glow filter for selected emotion */}
                    <filter id="glow" x="-30%" y="-30%" width="160%" height="160%">
                        <feGaussianBlur stdDeviation="3" result="blur" />
                        <feComposite in="SourceGraphic" in2="blur" operator="over" />
                    </filter>
                </defs>

                {/* Wheel background */}
                <circle
                    cx={WHEEL_CONFIG.centerX}
                    cy={WHEEL_CONFIG.centerY}
                    r={WHEEL_CONFIG.radius}
                    fill="url(#wheelGrad)"
                />

                {/* Light gradient overlay for better visibility */}
                <circle
                    cx={WHEEL_CONFIG.centerX}
                    cy={WHEEL_CONFIG.centerY}
                    r={WHEEL_CONFIG.radius}
                    fill="url(#wheelGrad)"
                    stroke="#b0d4f1"
                    strokeWidth="0.3"
                />


                {/* Crosshair lines */}
                <line
                    x1={WHEEL_CONFIG.centerX}
                    y1={WHEEL_CONFIG.centerY - WHEEL_CONFIG.radius}
                    x2={WHEEL_CONFIG.centerX}
                    y2={WHEEL_CONFIG.centerY + WHEEL_CONFIG.radius}
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth="1"
                />
                <line
                    x1={WHEEL_CONFIG.centerX - WHEEL_CONFIG.radius}
                    y1={WHEEL_CONFIG.centerY}
                    x2={WHEEL_CONFIG.centerX + WHEEL_CONFIG.radius}
                    y2={WHEEL_CONFIG.centerY}
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth="1"
                />

                {/* The marker circle */}
                {activeEmotion && (
                    <g>
                        <circle
                            className="emotion-marker-circle"
                            cx={markerPosition.x}
                            cy={markerPosition.y}
                            r={15}
                            fill="#fff"
                            stroke="rgba(255,255,255,0.1)"
                            strokeWidth="2"
                            filter="url(#glow)"
                        />
                    </g>
                )}

                {/* Draggable marker for when user is selecting */}
                {isDragging && (
                    <circle
                        cx={markerPosition.x}
                        cy={markerPosition.y}
                        r="10"
                        fill="#9dcbde"
                        stroke="#fff"
                        strokeWidth="1"
                        opacity="0.8"
                    />
                )}

                {/* Axis labels */}
                <text x={WHEEL_CONFIG.centerX} y="60" fontSize="var(--font-size-normal)" textAnchor="middle" fill="white" fontWeight="bold">
                    Aroused
                </text>
                <text x={WHEEL_CONFIG.centerX} y="330" fontSize="var(--font-size-normal)" textAnchor="middle" fill="white" fontWeight="bold">
                    Unaroused
                </text>
                <text
                    x="30"
                    y={WHEEL_CONFIG.centerY - 10}
                    fontSize="var(--font-size-normal)"
                    textAnchor="middle"
                    fill="white"
                    fontWeight="bold"
                    transform={`rotate(-90, 30, ${WHEEL_CONFIG.centerY})`}
                >
                    Negative
                </text>
                <text
                    x="270"
                    y={WHEEL_CONFIG.centerY - 7}
                    fontSize="var(--font-size-normal)"
                    textAnchor="middle"
                    fill="white"
                    fontWeight="bold"
                    transform={`rotate(90, 270, ${WHEEL_CONFIG.centerY})`}
                >
                    Positive
                </text>
            </svg>

            {/* Display selected emotion name */}
            <div style={{
                color: 'white',
                fontSize: 'var(--font-size-normal)',
                fontWeight: 'bold'
            }}>
                {activeEmotion ? activeEmotion.name : 'No emotion selected'}
            </div>

            {/* Show available emotions count in enhanced mode */}
            {availableEmotions && availableEmotions.length < 5 && (
                <div style={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: 'var(--font-size-normal)',
                    marginTop: '5px'
                }}>
                    {availableEmotions.length} emotions available for this character
                </div>
            )}

        </div>
    );
};

export default EmotionWheel;
